// Content for The Fourteen Stages of Bhakti Development
// Primary ślokas: SB 1.2.16–1.2.21 (doctrinal framework) + SB 1.5.24–28, 1.6.16 (Nārada's lived narrative)
// Commentary: Śrīla Viśvanātha Cakravartī Ṭhākura, Sārārtha-Darśinī

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySlokaHeader: 'SB 1.2.16\u20131.2.21 \u00b7 Vi\u015bvan\u0101tha Cakravart\u012b \u1e6ch\u0101kura\u2019s Summary Verse',
    primarySloka: {
        reference: 'Viśvanātha Cakravartī Ṭhākura, Sārārtha-Darśinī — Commentary on SB 1.2.21',
        sloka: `satāṁ kṛpā mahat-sevā
śraddhā guru-padāśrayaḥ |
bhajaneṣu spṛhā bhaktir
anarthāpagamas tataḥ |
niṣṭhā rucir athāsaktī
ratiḥ premātha darśanam |
harer mādhuryānubhava
ity arthāḥ syuś caturdaśe`,
        translation:
            'The fourteen stages are as follows: mercy of devotees (<strong>satāṁ kṛpā</strong>), service to great devotees (<strong>mahat-sevā</strong>), faith (<strong>śraddhā</strong>), surrender to guru (<strong>guru-padāśrayaḥ</strong>), desire for worship (<strong>bhajaneṣu spṛhā</strong>), bhakti (<strong>bhaktiḥ</strong>), clearance of anarthas (<strong>anarthāpagamaḥ</strong>), niṣṭhā, ruci, āsakti, rati, prema, seeing the Lord (<strong>darśanam</strong>), and experiencing the Lord\'s sweetness (<strong>harer mādhuryānubhava</strong>).',
    },
    blocks: [
        {
            type: 'text',
            content:
                'Śrīla Viśvanātha Cakravartī Ṭhākura\'s commentary on SB 1.2.16–21 lays out fourteen successive stages in the development of bhakti — beginning with the mercy of a pure devotee and ending with the direct experience of Kṛṣṇa\'s sweetness. Each stage is drawn from the verses of Śrīmad-Bhāgavatam itself, and Nārada Muni\'s account from Chapters 5 and 6 shows what it looks like when a soul actually passes through them.',
        },
        {
            type: 'divider',
            dividerLabel: 'Start of Commentary',
        },
        {
            type: 'heading',
            headingText: 'Stage 1: Mercy of Devotees (satāṁ kṛpā)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.24',
                sloka: `te mayy apetākhila-cāpale 'rbhake
dānte 'dhṛta-krīḍanake 'nuvartini |
cakruḥ kṛpāṁ yadyapi tulya-darśanāḥ
śuśrūṣamāṇe munayo 'lpa-bhāṣiṇi ||`,
                translation:
                    'Though the sages saw everything equally (<strong>tulya-darśanāḥ</strong>), they showed mercy to me (<strong>cakruḥ kṛpāṁ munayaḥ</strong>), though I was just a boy (<strong>arbhake</strong>) devoid of all fickleness (<strong>apeta-akhila-cāpale</strong>), with controlled senses (<strong>dānte</strong>), who had given up childish play (<strong>adhṛta-krīḍanake</strong>), who was obedient (<strong>anuvartini</strong>), attentive in their service (<strong>śuśrūṣamāṇe</strong>), and spoke little (<strong>alpa-bhāṣiṇi</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Nārada had controlled senses (<em>dānte</em>), had given up childish play (<em>adhṛta-kṛiḍanake</em>). Those sages saw equally good-tempered and bad-tempered, those who are praiseworthy and those who are to be criticized, those who have good conduct and those who are sinful (<em>tulya-darśanāḥ</em>), and thus they should not show mercy to one and reject in disgust another. However, they showed mercy to me. Though great devotees such as Bharata and Prahlāda see equally, they also show some partiality concerning giving mercy. Their first show of mercy did not depend on Nārada\'s good qualities and show of respect. He developed the good qualities by their mercy. Then those qualities became the cause of further mercy. It must be explained that the cause of their mercy was not due to seeing qualities. If one says that though they had equal vision, they showed mercy because he showed good qualities, then their equal vision would be contradicted by their seeing good and bad qualities. One should not explain in this way because then the first mercy would be prejudiced.<br /><br />There are two types of mercy; affected by seeing material qualities and not affected by seeing material qualities. The first type of mercy is explained as follows. All persons in the material world have mercy caused by qualities. If they see qualities they show mercy, if the qualities are absent, they withdraw mercy. And if they see bad qualities, they show hatred. The second type of mercy is as follows. Those who are beyond the influence of material world show mercy without such causes, without dependence on seeing material qualities, since they see everything in the material world as the same.<br /><br />Śukadeva has said: "During this season the mountains sometimes released their pure water and sometimes did not, just as experts in transcendental science sometimes give the nectar of transcendental knowledge and sometimes do not." (SB 10.20.36)<br /><br />In this manner sometimes these persons show mercy to some person. When hardness of the heart caused by <em>guṇas</em> is destroyed and becomes soft by devotion to the Lord, mercy will appear in the heart.<br /><br />That part of <em>bhakti</em> is called <em>bhāva</em>, whose essence is <em>saṁvit</em> and <em>hlādinī śakti</em>, which is one ray of the sun of <em>prema</em> which will soon rise in the heart, and which softens the heart with desires to meet, serve, and exchange love with the Lord. (Bhakti-rasāmṛta-sindhu 1.3.1)<br /><br />The first four stages of <em>bhakti</em> are understood here: mercy of the devotees, service to devotees, faith, and taking shelter of <em>guru</em>.',
        },
        {
            type: 'heading',
            headingText: 'Stages 2\u20134: Service to Great Devotees, Faith, and Surrender to Guru (mahat-sev\u0101 \u00b7 \u015braddhā \u00b7 guru-pad\u0101\u015braya\u1e25)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.16',
                sloka: `śuśrūṣoḥ śraddadhānasya
vāsudeva-kathā-ruciḥ |
syān mahat-sevayā viprāḥ
puṇya-tīrtha-niṣevaṇāt ||`,
                translation:
                    'O brāhmaṇas (<strong>viprāḥ</strong>)! Attraction for topics concerning Kṛṣṇa (<strong>vāsudeva-kathā-ruciḥ</strong>) will arise (<strong>syāt</strong>) by service to the great devotees (<strong>mahat-sevayā</strong>), followed by faith (<strong>śraddadhānasya</strong>), by surrender to the feet of the pure guru (<strong>puṇya-tīrtha-niṣevaṇāt</strong>), and by the desire to hear (<strong>śuśrūṣoḥ</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Hear how attraction for topics of the Lord arises! It arises by service to the great devotees, caused by the good fortune of mercy of those great devotees. By that one develops faith (<em>śraddadhānasya</em>). By faith one takes shelter of the feet of pure <em>guru</em> (<em>puṇya-tīrtha-niṣevanāt</em>). According to Amara-koṣa, <em>tīrtha</em> means water worshipped by sages, <em>guru</em>, path and cause. Being eager to hear those topics (<em>śuśrūṣoḥ</em>), he develops taste for those topics.',
        },
        {
            type: 'heading',
            headingText: 'Stage 5: Desire for Worship (bhajane\u1e63u sp\u1e5bh\u0101)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.25',
                sloka: `ucchiṣṭa-lepān anumodito dvijaiḥ
sakṛt sma bhuñje tad-apāsta-kilbiṣaḥ |
evaṁ pravṛttasya viśuddha-cetasas
tad-dharma evātma-ruciḥ prajāyate ||`,
                translation:
                    'One time only (<strong>sakṛt sma</strong>) I happened to eat (<strong>bhuñje</strong>) their food remnants (<strong>ucchiṣṭa-lepān</strong>) with their permission (<strong>anumoditaḥ dvijaiḥ</strong>). All obstacles to bhakti disappeared by that (<strong>tat-apāsta-kilbiṣaḥ</strong>). Having developed an inclination for bhakti (<strong>evaṁ pravṛttasya tat-dharma eva</strong>), being pure in mind (<strong>viśuddha-cetasaḥ</strong>), taste for devotional processes (<strong>ātma-ruciḥ</strong>) inevitably developed (<strong>prajāyate</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'After getting permission from them to do so, I ate one time some rice stuck to one of their dishes. By that all obstacles to <em>bhakti</em> were destroyed (<em>tad-apāsta-klibiṣaḥ</em>). Having taken their food, I developed without effort <em>bhakti</em> in the form of hearing, chanting and remembering the Lord continually. Having developed a tendency for <em>bhakti</em> after eating the devotees\' food (<em>evaṁ pravṛttasya</em>), which applies to anyone else as well, I inevitably developed a taste (<em>ruciḥ prajāyate</em>) for hearing and chanting (<em>tad-dharme</em>) in the mind (<em>ātma</em>). By this verse five stages of <em>bhakti</em> should be understood: desire for worship (<em>spṛhā</em>), <em>bhakti</em>, <em>anartha-nivṛtti</em>, <em>niṣṭhā</em> and ruci.',
        },
        {
            type: 'heading',
            headingText: 'Stage 6: Active Hearing, Chanting and Remembering (bhakti\u1e25)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.17',
                sloka: `śṛṇvatāṁ sva-kathāḥ kṛṣṇaḥ
puṇya-śravaṇa-kīrtanaḥ |
hṛdy antaḥ stho hy abhadrāṇi
vidhunoti suhṛt satām ||`,
                translation:
                    'Kṛṣṇa (<strong>kṛṣṇaḥ</strong>), who purifies through the processes of hearing and chanting (<strong>puṇya-śravaṇa-kīrtanaḥ</strong>), who is the true well-wisher of the devotees (<strong>suhṛt satām</strong>) who hear his topics (<strong>śṛṇvatāṁ sva-kathāḥ</strong>), enters (<strong>antaḥ sthaḥ hi</strong>) the hearts (<strong>hṛdi</strong>) of the devotees and destroys their sins (<strong>abhadrāṇi vidhunoti</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Then from hearing with taste, one begins to hear and chant. Then Kṛṣṇa, being situated within, destroys the sins (<em>abhadrāṇi</em>) in the heart. Kṛṣṇa being situated within refers to the process of <em>smaraṇam</em>, remembering.',
        },
        {
            type: 'heading',
            headingText: 'Stages 7\u20138: Clearance of Anarthas and Steadiness of Devotion (anarth\u0101pagama\u1e25 \u00b7 ni\u1e63\u1e6dh\u0101)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.18',
                sloka: `naṣṭa-prāyeṣv abhadreṣu
nityaṁ bhāgavata-sevayā |
bhagavaty uttama-śloke
bhaktir bhavati naiṣṭhikī ||`,
                translation:
                    'As the impediments to bhakti (<strong>abhadreṣu</strong>) become generally destroyed (<strong>naṣṭa-prāyeṣu</strong>) by constant service (<strong>nityaṁ sevayā</strong>) to the devotees and Bhāgavatam (<strong>bhāgavata</strong>), the stage of niṣṭhitā bhakti (<strong>naiṣṭhikī bhaktiḥ</strong>) to Bhagavān (<strong>bhagavati</strong>), who is praised by the greatest sages (<strong>uttama-śloke</strong>), becomes established (<strong>bhavati</strong>).',
            },
        },
        {
            type: 'text',
            content:
                '<em>Bhāgavata-sevayā</em> means by service to the devotees of Bhagavān and service to (such as hearing) the Śrīmad-bhāgavatam. By serving the devotees and Bhāgavatam, the major portion of <em>nāmāparādhas</em> (<em>abhadreṣu</em>) becomes weak. This weakening of <em>aparādhas</em> continues till the stage of rati (<em>bhāva</em>). <em>Naiṣṭhikī</em> refers to <em>niṣṭhā</em>, where the mind can easily concentrate on the Lord. When the anarthas are for the most part destroyed, the devotee attains the stage of <em>niṣṭhā</em>.',
        },
        {
            type: 'heading',
            headingText: 'Stage 9: Ruci — True Taste',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.19',
                sloka: `tadā rajas-tamo-bhāvāḥ
kāma-lobhādayaś ca ye |
ceta etair anāviddhaṁ
sthitaṁ sattve prasīdati ||`,
                translation:
                    'The mind (<strong>cetaḥ</strong>) becomes unaffected (<strong>anāviddham</strong>) by lust (<strong>kāma</strong>), greed (<strong>lobha-ādayaḥ</strong>), and other impulses arising from rajas and tamas (<strong>tadā rajaḥ-tamaḥ-bhāvāḥ ye</strong>). Then the mind becomes fixed in the śuddha-sattva form of the Lord (<strong>sthitaṁ sattve</strong>) and becomes satisfied (<strong>prasīdati</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'The mind is not transformed (<em>anāviddham</em>) by the agitations of lust, greed and other qualities such as anger, illusion, and hatred, which arise from (<em>bhāvāḥ</em>) rajas and tamas. This means that by lack of taste for material objects, the state of ruci — the appearance of relishing of hearing, chanting and other processes — arises. In the previous state, the mind was pierced by the sharp arrows of lust and greed etc. How could the mind be satisfied in that state? How could the mind attain real taste for chanting in that state? A person afflicted with pain cannot relish food.',
        },
        {
            type: 'heading',
            headingText: 'Stage 10: Āsakti — Attachment to the Lord',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.26',
                sloka: `tatrānvahaṁ kṛṣṇa-kathāḥ pragāyatām
anugraheṇāśṛṇavaṁ manoharāḥ |
tāḥ śraddhayā me 'nupadaṁ viśṛṇvataḥ
priyaśravasy aṅga mamābhavad ruciḥ ||`,
                translation:
                    'By the mercy of the sages (<strong>anugraheṇa</strong>) who were chanting (<strong>pragāyatām</strong>) the attractive pastimes of Kṛṣṇa (<strong>kṛṣṇa-kathāḥ manoharāḥ</strong>) daily (<strong>anu-aham</strong>), I heard in that place with āsakti. Hearing constantly at every step (<strong>tāḥ śraddhayā me anupadaṁ viśṛṇvataḥ</strong>), I then developed rati for Kṛṣṇa, whose qualities are most pleasing to hear (<strong>priyaśravasi aṅga mama abhavad ruciḥ</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'After this, the mind becomes fixed (sthitam) in the <em>śuddha-sattva</em> deity of the Lord (sattve). This is the stage of <em>āsakti</em>.<br /><br /><em>Śraddha</em> here indicates the tenth stage <em>āsakti</em>. At every moment or at hearing every word, I developed rati (<em>bhāva</em>) for Kṛṣṇa, whose qualities are pleasing to hear (<em>priya-śravasi</em>). Ruci should here mean rati, the eleventh stage, since the stage of ruci has already been mentioned in the previous verse.',
        },
        {
            type: 'heading',
            headingText: 'Stage 11: Rati / Bhāva — Divine Emotion',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.20',
                sloka: `evaṁ prasanna-manaso
bhagavad-bhakti-yogataḥ |
bhagavat-tattva-vijñānaṁ
mukta-saṅgasya jāyate ||`,
                translation:
                    'The mind becomes joyful and satisfied (<strong>evaṁ prasanna-manasaḥ</strong>) on attaining rati. Finally prema develops (<strong>bhagavad-bhakti-yogataḥ</strong>), accompanied by the appearance of complete detachment (<strong>mukta-saṅgasya</strong>). The devotee then experiences (<strong>jāyate</strong>) the Lord\'s form, qualities, pastimes, powers and sweetness (<strong>bhagavat-tattva-vijñānam</strong>).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.27',
                sloka: `tasmiṁs tadā labdha-rucer mahā-mate
priyaśravasy askhalitā matir mama |
yayāham etat sad-asat sva-māyayā
paśye mayi brahmaṇi kalpitaṁ pare ||`,
                translation:
                    'Having attained an excellent taste for the Lord (<strong>tasmin tadā labdha-ruceḥ</strong>), O wise one, my intelligence did not waver from Him (<strong>priyaśravasi askhalitā matiḥ mama mahā-mate</strong>). By my intelligence (<strong>yayā aham</strong>), I directly experienced (<strong>paśye</strong>) that my gross and subtle bodies (<strong>etat sat-asat</strong>), created by the Lord\'s material energy (<strong>sva-māyayā</strong>), were fixed only in the Supreme Lord (<strong>mayi brahmaṇi kalpitaṁ pare</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'By this method, with the development of <em>āśakti</em>, with worship of Kṛṣṇa at every moment, rati then appears (<em>prasanna-manasaḥ</em>). Without rati, there could never be constant detachment from objects of material enjoyment, and without constant detachment, there could never be satisfaction of the mind.<br /><br />Having attained an excellent taste, my intelligence became steadily fixed in the Lord. By that intelligence I saw the gross and subtle body (sad-asat) existing in me by the influence of the Lord\'s material energy to be established in Kṛṣṇa (<em>brahmaṇi</em>). The gross body became fixed in offering respects, carrying the Lord\'s water pot and other actions, not in material actions for oneself. The subtle body with senses such as ear, eye, mind and intelligence became fixed in tasting the sweetness of the lord\'s qualities and form, not in material objects for one\'s own pleasure. Previously the eye and mind were not fixed in the Lord even by exerting much effort. That is not the case now. With the development of rati, the mind and other senses, giving up the practice of fixing themselves on their material sense objects to which they had been long accustomed, became automatically fixed only on the Lord. Thus "I see" here means "directly experience."',
        },
        {
            type: 'heading',
            headingText: 'Stage 12: Prema — Divine Love',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.28',
                sloka: `itthaṁ śarat-prāvṛṣikāv ṛtū harer
viśṛṇvato me 'nusavaṁ yaśo 'malam |
saṅkīrtyamānaṁ munibhir mahātmabhir
bhaktiḥ pravṛttātma-rajas-tamopahā ||`,
                translation:
                    'In this way (<strong>ittham</strong>), for the two seasons of monsoon and autumn (<strong>śarat-prāvṛṣikau ṛtū</strong>), I constantly heard (<strong>me anusavaṁ viśṛṇvataḥ</strong>) the pure glories of the Lord (<strong>hareḥ amalam yaśaḥ</strong>) chanted by the generous sages (<strong>saṅkīrtyamānam munibhiḥ mahātmabhiḥ</strong>). Prema then appeared (<strong>bhaktiḥ pravṛttā</strong>), which destroys rajas and tamas in the jīvas (<strong>ātma-rajaḥ-tamaḥ-upahā</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'Then <em>prema</em> to Bhagavān appears (<em>bhagavad-bhakti-yogataḥ</em>).<br /><br />Extending over two seasons (four months) I heard constantly (anusavam). Premā (<em>bhaktiḥ</em>) which destroys (<em>apahā</em>) rajas and tamas in all <em>jīvas</em> (<em>ātma</em>) then appeared. It is implied here that there was destruction of rajas and tamas in others as well who realized devotion to the Lord. This is the twelfth stage of <em>bhakti</em>. Direct vision of the Lord and the experience of sweetness will be explained in the next chapter.',
        },
        {
            type: 'heading',
            headingText: 'Stage 13: Seeing the Lord (dar\u015banam)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.21',
                sloka: `bhidyate hṛdaya-granthiś
chidyante sarva-saṁśayāḥ |
kṣīyante cāsya karmāṇi
dṛṣṭa evātmanīśvare ||`,
                translation:
                    'Ignorance is cut (<strong>bhidyate hṛdaya-granthiḥ</strong>) and all doubts are destroyed (<strong>chidyante sarva-saṁśayāḥ</strong>). On seeing the Lord within the mind and with the eyes (<strong>dṛṣṭe eva ātmani īśvare</strong>), all karmas are destroyed (<strong>kṣīyante ca asya karmāṇi</strong>).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.6.16',
                sloka: `dhyāyataś caraṇāmbhojaṁ
bhāva-nirjita-cetasā |
autkaṇṭhyāśru-kalākṣasya
hṛdy āsīn me śanair hariḥ ||`,
                translation:
                    'As I meditated (<strong>dhyāyataḥ</strong>) on the lotus feet of the Lord (<strong>caraṇa-ambhojam</strong>) with a mind conquered by prema (<strong>bhāva-nirjita-cetasā</strong>), with tears in my eyes from longing (<strong>autkaṇṭhya-aśru-kala-akṣasya</strong>), step by step (<strong>śanaiḥ</strong>) the Lord (<strong>hariḥ</strong>) made his appearance in my mind (<strong>hṛdy āsīt me</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'The knot in the heart, ignorance, is cut. This is a reflexive verb form (<em>karma-kartari</em>) which indicates that destruction of ignorance is not the main result sought in <em>bhakti</em>. All doubts, such as thinking that the attainment is impossible, are destroyed. Having seen the Lord, who is the soul (<em>ātmani</em>), all karmas are destroyed. Or the Lord, being in the mind (<em>ātmani</em>), and then being seen directly, all karmas are destroyed. Thus both the sphurti of the Lord in the mind and direct appearance to the eyes are indicated.<br /><br />As I meditated in the mind (<em>hṛdi</em>) with the mind (<em>cetasā</em>) conquered by <em>prema</em> (<em>bhāva-nirjita</em>), the Lord step by step made his appearance before me (<em>āsīt</em>). Or <em>śanaiḥ</em> (gradually) can mean that first he appeared in the heart, and then he appeared in the three functions of the mind — nose, ear and eye, so that I could experience the fragrance of his body, the sound of his ankle bells and the beauty of his face. What happened to me? I had tears in my eyes because of longing.',
        },
        {
            type: 'heading',
            headingText: 'Stage 14: Experiencing the Lord\'s Sweetness (harer m\u0101dhury\u0101nubhava)',
        },
        {
            type: 'text',
            content:
                'From that arises realization (<em>vijñānam</em>) of the Lord\'s form, qualities, pastimes, powers and sweetness. The desired result of <em>bhakti</em> has thus been explained. Accompanying results were previously mentioned with janayaty <em>āśu vairāgyam jñānam ca yad</em> ahaitukam. Here also they are mentioned with the phrase "appearance of <em>vairāgya</em>" (<em>mukta-saṅgasya</em>).',
        },
        {
            type: 'divider',
            dividerLabel: 'End of Commentary',
        },
        {
            type: 'divider',
            dividerLabel: 'The Wise Perform Bhakti with Great Joy',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.22',
                sloka: `ato vai kavayo nityaṁ
bhaktiṁ paramayā mudā |
vāsudeve bhagavati
kurvanty ātma-prasādanīm ||`,
                translation:
                    'Thus (<strong>ataḥ vai</strong>) the wise (<strong>kavayaḥ</strong>) constantly (<strong>nityam</strong>) perform bhakti (<strong>bhaktim kurvanti</strong>) — which gives joy to the mind (<strong>ātma-prasādanīm</strong>) — to Lord Kṛṣṇa (<strong>vāsudeve bhagavati</strong>) with great joy (<strong>paramayā mudā</strong>).',
            },
        },
        {
            type: 'text',
            content:
                'By saying "with great joy" it is indicated that even at the stage of <em>sādhana-bhakti</em> there are no difficulties.',
        },
    ],
};

export default content;
